import { create } from 'zustand';
import type { GuideStep } from './guide-types';
import { GUIDE_STORAGE_PREFIXES } from './storage-keys';

/**
 * 가이드 시스템 localStorage 키의 단일 진실 공급원은 `./storage-keys.ts` 참조.
 * 모든 prefix는 `wkwk:guide:*` 콜론 네임스페이스로 통일.
 *
 * key 목록 (dismissed/auto-shown): home, gsr-home, long-to-short, multi-to-one,
 * output, gts, source-to-content, ai-video, export, insight, brand-template,
 * image-editor, editor
 */
export const STORAGE_PREFIX = GUIDE_STORAGE_PREFIXES.dismissed;
/** localStorage prefix: 세션 최초 1회 자동 노출 여부 플래그 */
export const AUTO_SHOWN_PREFIX = GUIDE_STORAGE_PREFIXES.autoShown;

/**
 * 해당 key의 가이드가 이미 자동 노출된 적이 있는지 확인.
 * dismissed와 별개로, 자동 시작 억제 용도.
 */
export function hasAutoShown(key: string): boolean {
  if (typeof window === 'undefined') return false;
  // Safari Private Mode 등 localStorage 접근 실패 시 false 반환 → 가이드 1회 노출 허용.
  try {
    return localStorage.getItem(`${AUTO_SHOWN_PREFIX}${key}`) === 'true';
  } catch {
    return false;
  }
}

/**
 * 자동 노출이 발생했음을 기록 (1회성 자동 시작 제한용).
 * 저장 실패 시(quota/private mode) 다음 진입에서 가이드가 한 번 더 자동 노출될 수 있다.
 */
export function markAutoShown(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`${AUTO_SHOWN_PREFIX}${key}`, 'true');
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[guide] failed to persist auto-shown for "${key}":`, e);
    }
  }
}

/**
 * 세션 내에서 스킵/완료된 가이드 키 (새로고침 시 리셋).
 *
 * 모듈 스코프로 의도적으로 유지: Zustand state에 두면 `discardActiveGuide`의
 * `INITIAL_STATE` 리셋에 휩쓸려 "라우트 전환으로 잠시 닫고 재시작" 시나리오가 깨진다.
 *
 * 알려진 한계:
 * - 같은 탭에서 계정을 전환해도 이 Set은 유지됨 → 계정 A가 skip한 가이드를
 *   계정 B가 못 보는 누출. 현재 계정 전환이 풀 페이지 리로드를 동반하므로
 *   실제 영향은 제한적 (추측 — 런타임 확인 필요).
 * - HMR/Fast Refresh 시 모듈 재평가로 Set이 초기화되나 dev-only 영향.
 * - 테스트 환경에서는 테스트 간 격리를 위해 명시적 리셋 필요.
 *
 * 증상 발견 시: `clearSessionSkipped()` export 추가 후 계정 전환 훅에서 호출.
 */
const sessionSkipped = new Set<string>();

interface GuideState {
  /** 현재 활성 가이드 키 */
  activeGuideKey: string | null;
  /** 필터링된 실제 표시 스텝 목록 (visibleSteps 기준으로 인덱스 관리) */
  visibleSteps: GuideStep[];
  /** 현재 스텝 인덱스 (visibleSteps 기준) */
  currentStep: number;
  /** "다음부터 안보기" 체크 상태 */
  dismissChecked: boolean;
  /** 가이드 리셋 카운터 (변경 시 useGuide 재실행) */
  resetCount: number;

  /** 가이드 시작. 실제 활성화 성공 시 true 반환 (dismissed/sessionSkipped/모든 스텝 skip 시 false). */
  startGuide: (key: string, steps: GuideStep[]) => boolean;
  nextStep: () => void;
  prevStep: () => void;
  /** 현재 활성 가이드의 visibleSteps를 갱신 (뷰포트 변경 등에 대응). 현재 스텝 유지 시 인덱스 재매핑. */
  updateVisibleSteps: (steps: GuideStep[]) => void;
  skipGuide: () => void;
  completeGuide: () => void;
  setDismissChecked: (checked: boolean) => void;
  /** 특정 가이드의 dismiss 상태를 초기화하여 다시 표시 */
  resetGuide: (key: string) => void;
  /**
   * 현재 활성 가이드를 단순히 닫는다 (sessionSkipped 추가하지 않음).
   * 라우트 전환 등으로 다른 가이드가 이어서 시작되어야 할 때 사용.
   */
  discardActiveGuide: () => void;
}

/**
 * skipIfNotFound 스텝의 타겟이 현재 DOM에 존재하는지 확인.
 * 타겟이 없으면 스킵 대상 (false 반환).
 */
function isStepAvailable(step: GuideStep | undefined): boolean {
  if (!step) return false;
  if (step.skipIfNotFound && step.target) {
    if (typeof document === 'undefined') return true;
    return !!document.querySelector(step.target);
  }
  return true;
}

/** from 인덱스부터 앞으로 탐색하여 사용 가능한 첫 스텝 인덱스 반환 (-1 if none). */
function findNextAvailableStep(steps: GuideStep[], from: number): number {
  for (let i = Math.max(0, from); i < steps.length; i++) {
    if (isStepAvailable(steps[i])) return i;
  }
  return -1;
}

/** from 인덱스부터 뒤로 탐색하여 사용 가능한 첫 스텝 인덱스 반환 (-1 if none). */
function findPrevAvailableStep(steps: GuideStep[], from: number): number {
  for (let i = Math.min(steps.length - 1, from); i >= 0; i--) {
    if (isStepAvailable(steps[i])) return i;
  }
  return -1;
}

function isDismissed(key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${key}`) === 'true';
  } catch {
    return false;
  }
}

/**
 * dismiss 상태 저장. quota exceeded/private mode 등으로 실패해도
 * finishGuide 경로를 터뜨리지 않도록 try/catch로 격리.
 */
function setDismissed(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, 'true');
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[guide] failed to persist dismissed for "${key}":`, e);
    }
  }
}

function finishGuide(key: string, dismissChecked: boolean) {
  sessionSkipped.add(key);
  if (dismissChecked) {
    setDismissed(key);
  }
}

const INITIAL_STATE = {
  activeGuideKey: null,
  visibleSteps: [] as GuideStep[],
  currentStep: 0,
  dismissChecked: false,
  resetCount: 0,
};

export const useGuideStore = create<GuideState>((set, get) => ({
  ...INITIAL_STATE,

  startGuide: (key, steps) => {
    if (isDismissed(key)) return false;
    if (sessionSkipped.has(key)) return false;
    // 시작 시점에 skipIfNotFound 스텝 중 타겟이 없는 스텝은 건너뛴다.
    const startIndex = findNextAvailableStep(steps, 0);
    if (startIndex === -1) {
      // 모든 스텝이 스킵 대상이면 가이드를 시작하지 않음.
      // DOM 셀렉터가 깨졌거나 모든 조건부 UI가 비활성인 상태. 운영자에게 진단 단서 제공.
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[guide] "${key}" skipped — no available step (all targets missing or skipIfNotFound).`,
        );
      }
      return false;
    }
    set({
      activeGuideKey: key,
      visibleSteps: steps,
      currentStep: startIndex,
      dismissChecked: false,
    });
    return true;
  },

  nextStep: () => {
    const { visibleSteps, currentStep } = get();
    const nextIndex = findNextAvailableStep(visibleSteps, currentStep + 1);
    if (nextIndex === -1) {
      get().completeGuide();
    } else {
      set({ currentStep: nextIndex });
    }
  },

  prevStep: () => {
    const { visibleSteps, currentStep } = get();
    const prevIndex = findPrevAvailableStep(visibleSteps, currentStep - 1);
    if (prevIndex !== -1) {
      set({ currentStep: prevIndex });
    }
  },

  updateVisibleSteps: (steps) => {
    const { activeGuideKey, visibleSteps, currentStep } = get();
    if (!activeGuideKey) return;
    // 현재 표시 중인 스텝을 가능한 유지 (title+target 기준 매칭).
    const current = visibleSteps[currentStep];
    let nextIndex = current
      ? steps.findIndex(
          (s) => s.title === current.title && s.target === current.target,
        )
      : -1;
    if (nextIndex === -1) {
      // 현재 스텝이 새 목록에 없으면 가까운 다음 available 스텝으로.
      nextIndex = findNextAvailableStep(steps, 0);
    } else if (!isStepAvailable(steps[nextIndex])) {
      nextIndex = findNextAvailableStep(steps, nextIndex);
    }
    if (nextIndex === -1 || steps.length === 0) {
      get().completeGuide();
      return;
    }
    set({ visibleSteps: steps, currentStep: nextIndex });
  },

  skipGuide: () => {
    const { activeGuideKey, dismissChecked } = get();
    if (activeGuideKey) finishGuide(activeGuideKey, dismissChecked);
    set((state) => ({
      ...INITIAL_STATE,
      resetCount: state.resetCount + 1,
    }));
  },

  completeGuide: () => {
    const { activeGuideKey, dismissChecked } = get();
    if (activeGuideKey) finishGuide(activeGuideKey, dismissChecked);
    set((state) => ({
      ...INITIAL_STATE,
      resetCount: state.resetCount + 1,
    }));
  },

  setDismissChecked: (checked) => {
    set({ dismissChecked: checked });
  },

  discardActiveGuide: () => {
    // sessionSkipped 건드리지 않음 — 세션 내 재시작 허용
    set((state) => ({
      ...INITIAL_STATE,
      resetCount: state.resetCount + 1,
    }));
  },

  resetGuide: (key) => {
    // localStorage 예외(quota/private mode)가 리셋 흐름을 터뜨리지 않도록 try/catch.
    try {
      localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
      // autoShown 플래그 제거 — 수동 "가이드 다시 보기" 클릭 시 useGuide의
      // !hasAutoShown 가드를 통과하여 재실행 가능하게 함. 시작 직후 markAutoShown이
      // 다시 호출되므로 다음 페이지 재방문 시에는 자동 노출되지 않는다.
      localStorage.removeItem(`${AUTO_SHOWN_PREFIX}${key}`);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(`[guide] failed to reset localStorage for "${key}":`, e);
      }
    }
    // 세션 스킵 목록에서도 제거
    sessionSkipped.delete(key);
    // resetCount 증가로 useGuide 재실행 트리거
    set((state) => ({ resetCount: state.resetCount + 1 }));
  },
}));
